import { useState, useEffect } from "react";
import { useApiClient } from "./useApiClient";
import { apiRoutes } from "../api/routes";

/**
 * Polls the count of active (unresolved) emergency alerts (type = 13).
 *
 * Mirrors the legacy `status_emergency_count` statistic from
 * `POST /alert/statistics_query`:
 *   query: [["null","resolve_timestamp"],["equal","type",13]]
 *
 * No date-range filter is applied — emergencies are tracked regardless of
 * when they were created, until they are resolved.
 *
 * Polled every 60 s to match the cadence of the other navbar badge hooks.
 * Errors are swallowed so a transient failure does not reset the badge to 0.
 */
export const useEmergencyCount = () => {
  const [emergencyCount, setEmergencyCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const apiClient = useApiClient();

  useEffect(() => {
    let isMounted = true;

    const fetchEmergencyCount = async () => {
      try {
        const requestBody = {
          select: ["id"],
          query: [
            ["null", "resolve_timestamp"],
            ["equal", "type", 13],
          ],
          options: {
            timeout: 10,
            limit: 0,
            calculate_total: true,
            return_results: false,
            select_distinct: true,
          },
        };

        const response = await apiClient.post(
          apiRoutes.proxy("support/internal/alert/select_query"),
          requestBody,
        );

        if (!isMounted) return;
        if (response.data && response.data.info) {
          setEmergencyCount(response.data.info.total_results ?? 0);
          setError(null);
        } else {
          setError("Failed to fetch emergency count");
        }
      } catch (err: unknown) {
        if (!isMounted) return;
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred");
        }
        // Fail silently — keep the previous count.
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchEmergencyCount();
    const interval = setInterval(fetchEmergencyCount, 60000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [apiClient]);

  return { emergencyCount, isLoading, error };
};